import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { PageShell } from "@/components/PageShell";
import { surgeries, specialties, locations } from "@/lib/healic-data";
import { SurgeryPage } from "@/routes/surgeries.$slug";
import { loadLegacyContent, LegacyContent, legacyHead, type LegacyPayload } from "@/lib/legacy-content";
import { getCitySections, getCityFaqs } from "@/lib/surgery-city-content";


const SITE_URL = "https://healic.in";

function shortCity(city: string): string {
  return city.split(",")[0]!.trim();
}

/**
 * Legacy URLs we now serve directly (no redirect) — same URL as the old healic.in site.
 * Each maps the old slug to a (surgery, location, intro) triplet for a city-specific page.
 */
const LEGACY_LOCATION_PAGES: Record<
  string,
  { surgery: string; location: string; heading: string; intro: string }
> = {
  "circumcision-surgery-in-gurgaon": {
    surgery: "circumcision",
    location: "sohna-road-gurugram",
    heading: "Circumcision Surgery in Gurgaon",
    intro:
      "Healic Gurgaon (Sohna Road) is one of the most trusted centres for ZSR-stapler and laser circumcision in the NCR. Patients from Sohna Road, Sector 49, Badshahpur, Golf Course Extension and across Gurugram choose us for our walk-in, walk-out daycare protocol — most adults are home in 2–3 hours, back to desk work within 48 hours, and back to driving within a week.",
  },
  "circumcision-surgery-in-agra": {
    surgery: "circumcision",
    location: "agra",
    heading: "Circumcision Surgery in Agra",
    intro:
      "Healic Agra performs stitch-free laser and stapler circumcision for boys and adults from across Agra, Fatehabad Road, Sikandra, Dayalbagh and nearby towns of Mathura, Firozabad and Etah. Our urology and general-surgery team handles phimosis, recurrent balanitis and elective cases with full cashless insurance support.",
  },
  "circumcision-surgery-in-haridwar": {
    surgery: "circumcision",
    location: "haridwar",
    heading: "Circumcision Surgery in Haridwar",
    intro:
      "Patients from Haridwar, Roorkee, Jwalapur, Kankhal and Rishikesh trust Healic Haridwar for modern ZSR and laser circumcision. We offer English-speaking coordinators, transparent fixed-price packages and same-day surgery slots — ideal for travelling patients who want the procedure done and dusted in one visit.",
  },
  "circumcision-in-ghaziabad": {
    surgery: "circumcision",
    location: "indirapuram-ghaziabad",
    heading: "Circumcision in Ghaziabad",
    intro:
      "Healic Indirapuram is our Ghaziabad centre for circumcision — covering Vaishali, Vasundhara, Kaushambi, Noida Extension and Crossing Republik. We use ZSR stapler and laser techniques that are stitch-free, virtually painless, and let most patients return to school or office within 2–3 days.",
  },
  "circumcision-in-greater-noida": {
    surgery: "circumcision",
    location: "indirapuram-ghaziabad",
    heading: "Circumcision Surgery in Greater Noida",
    intro:
      "Healic serves Greater Noida, Noida Extension, Pari Chowk, Knowledge Park, Alpha, Beta and Gamma sectors from our Indirapuram centre — a short drive across the DND/FNG. We offer ZSR-stapler and laser circumcision as a true daycare procedure: walk in, walk out the same day, back to office in 48 hours and back to driving within a week.",
  },
};

type LoaderResult =
  | {
      kind: "city";
      surgery: NonNullable<ReturnType<typeof surgeries.find>>;
      specialty: ReturnType<typeof specialties.find>;
      related: typeof surgeries;
      location: NonNullable<ReturnType<typeof locations.find>>;
      cfg: (typeof LEGACY_LOCATION_PAGES)[string];
    }
  | { kind: "legacy"; payload: LegacyPayload };

export const Route = createFileRoute("/general-surgeries/$slug")({
  loader: ({ params }): LoaderResult => {
    const cfg = LEGACY_LOCATION_PAGES[params.slug];
    if (cfg) {
      const surgery = surgeries.find((s) => s.slug === cfg.surgery);
      const location = locations.find((l) => l.slug === cfg.location);
      if (!surgery || !location) throw notFound();
      const specialty = specialties.find((sp) => sp.slug === surgery.specialtySlug);
      const related = surgeries
        .filter((s) => s.specialtySlug === surgery.specialtySlug && s.slug !== surgery.slug)
        .slice(0, 4);
      return { kind: "city", surgery, specialty, related, location, cfg };
    }
    return { kind: "legacy", payload: loadLegacyContent(`general-surgeries/${params.slug}`) };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [{ title: "Healic" }] };
    if (loaderData.kind === "legacy") {
      return legacyHead(`/general-surgeries/${params.slug}`, loaderData.payload);
    }
    const { surgery, location, cfg } = loaderData;
    const city = shortCity(location.city);
    const path = `/general-surgeries/${params.slug}`;
    const url = `${SITE_URL}${path}`;
    const title = `${cfg.heading} — Cost, Best Doctors & Recovery | Healic`;
    const description = `${cfg.heading} at Healic ${city}. ${surgery.short} Fixed-price packages, expert surgeons & cashless insurance.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
        { name: "geo.placename", content: city },
        { property: "og:title", content: `${cfg.heading} — Healic` },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { property: "og:locale", content: "en_IN" },
        { property: "og:site_name", content: "Healic Multispeciality Hospital" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: `${cfg.heading} — Healic` },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MedicalProcedure",
            name: cfg.heading,
            description: surgery.overview,
            procedureType: "https://schema.org/SurgicalProcedure",
            howPerformed: surgery.procedure.join(". "),
            preparation: "Pre-operative consultation, blood work and anaesthesia review.",
            followup: surgery.recovery,
            url,
            location: { "@type": "Hospital", name: `Healic ${city}`, address: location.address },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: surgery.faqs.map((f: { q: string; a: string }) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <PageShell>
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-display text-3xl font-bold">Page not found</h1>
        <Link to="/surgeries" className="mt-6 inline-block text-accent">Browse all surgeries →</Link>
      </div>
    </PageShell>
  ),
  errorComponent: () => (
    <PageShell>
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-display text-3xl font-bold">Something went wrong</h1>
      </div>
    </PageShell>
  ),
  component: LegacyGeneralSurgeryPage,
});

function LegacyGeneralSurgeryPage() {
  const data = Route.useLoaderData();
  if (data.kind === "legacy") return <LegacyContent payload={data.payload} />;
  const { surgery, specialty, related, location, cfg } = data;
  const city = shortCity(location.city);
  // Pull in the same city-specific SEO sections used by /surgeries/<slug>/<city>
  // so legacy URLs like /general-surgeries/circumcision-surgery-in-gurgaon
  // also carry unique, location-aware content for crawlers — rendered inside
  // the same rich SurgeryPage layout used by /surgeries/<slug>.
  const citySections = getCitySections(surgery, location);
  const cityFaqs = getCityFaqs(surgery, location);

  // Enrich the surgery so hero H1, About heading and JSON-LD read as the
  // city page ("… in Gurgaon") while all imagery/sections stay identical
  // to the main /surgeries/<slug> page.
  const enrichedSurgery = {
    ...surgery,
    name: cfg.heading,
    short: `${surgery.short} Trusted by patients across ${city} — fixed-price packages, expert surgeons and end-to-end care at Healic ${city}.`,
    overview: `${cfg.intro}\n\n${surgery.overview}`,
    faqs: [...cityFaqs, ...surgery.faqs],
  };

  const extraSections =
    citySections.length > 0 ? (
      <div className="space-y-12">
        {citySections.map((s) => (
          <div key={s.heading}>
            <h2 className="font-display text-2xl font-bold">{s.heading}</h2>
            <p className="mt-3 text-muted-foreground">{s.body}</p>
            {s.bullets && (
              <ul className="mt-5 grid gap-3 sm:grid-cols-2">
                {s.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-3 rounded-2xl bg-cream p-4">
                    <span className="mt-0.5 inline-block h-2 w-2 flex-none rounded-full bg-accent" />
                    <span className="text-sm font-medium">{b}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    ) : null;

  return (
    <SurgeryPage
      data={{ surgery: enrichedSurgery, specialty, related, extraSections }}
    />
  );
}



